// 클레임 엑셀 일괄등록(구성안 화면② · req-20261102-095427-26acf0 P4/4) — 머리 [← 단건 등록] [① 양식 다운로드(.xlsx)]
// · ② 엑셀 업로드(끌어다 놓기 · [파일 선택] · 진행률) · ③ 검증 결과 표(필터 칩 · 50행 페이지 · 체크 · 증빙 [첨부] · 행 클릭 → 상세 패널)
// · 오른쪽 상세 패널(건별 수정 · 사진) · ⑤ 일괄 검증 요약 · ⑥ 증빙사진 첨부 현황 · ⑦ 선택 요약 · [선택 건 일괄 등록] · [임시저장] · [취소]
// · 일괄 등록 결과 · ?upload= 가 없으면 업로드 카드 + 업로드 이력.
// 서버 routers/claim_bulk.py(/api/claims/bulk) — 업로드 202 → 백그라운드 검증 · 등록 202 → 백그라운드 등록 · GET /{id} 로 진행률.
// 🔴 판정·금액·기한·승인은 서버 값만 그린다(화면에서 셈하지 않는다) — 상세 [적용] = patchRow 응답으로 표·요약을 다시 그린다.
// 🔴 queued·validating·registering 이면 1.5초마다 GET /{id} — 마지막 요청의 응답만 쓴다 · 화면은 막지 않는다.
// 🔴 DELETE 요청은 없다 — 취소 = POST …/cancel · 사진 지우기·보기 = claimLedgerAPI.removePhoto·photo(행의 claim_id). 다운로드는 blob.
// 🔴 오류 행 송장을 누르면 단건 화면 /claims/register?bulk_upload&bulk_row 로 간다(그 행 값으로 채우고 조회를 다시 부르지 않는다).
import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { IconFileInvoice } from '@tabler/icons-react';
import AppShell from '../components/AppShell';
import { Page, PageHeader, Button } from '../components/ui';
import { claimBulkAPI, claimLedgerAPI } from '../api/client';
import { confirmDialog, toast } from '../components/AppDialog';
import { fmtKstShort } from '../utils/kstTime';
import { blobDownload } from '../utils/meetingUtil';
import { COLORS, TONES, badge, btn, card, input } from '../utils/uiStyles';
import { errorText, fmtWon, labelOf, statusOf } from './claims/claimLabels';
import { courierDeadlineText, optionsWithCurrent, shipperDeadlineText } from './claims/claimRegisterUtil';
import {
  ACCIDENT_LABEL, ACCIDENT_SHORT, ACCIDENT_TONE, FAILED_PAGE, LEVEL_FILTERS, PAGE_SIZE, POLL_MS, UPLOAD_STATUS,
  UPLOAD_STATUS_TONE, canSelectRow, checkUploadFile, evidenceProgress, fileNameOf, fixPath, historyResultText, isLive,
  panelEdits, panelFromDetail, progressOf, registerConfirmText, resultText, rowOf, rowStatus, selectionText,
  summaryCounts, uploadInfoText,
} from './claims/claimBulkUtil';

const TEMPLATE_FALLBACK = '클레임_일괄등록_양식.xlsx';
const CANCEL_CONFIRM = '이 업로드를 취소합니다. 등록하지 않은 행과 그 임시저장 원장(사진 포함)이 지워집니다. 이미 등록된 클레임은 남습니다.';
const EMPTY_ROWS = { total: 0, offset: 0, limit: PAGE_SIZE, items: [] };
const ZONE_OPTIONS = [
  { key: 'ambient', label: '상온' }, { key: 'cold', label: '냉장·냉동' }, { key: 'chilled', label: '냉장' }, { key: 'frozen', label: '냉동' },
];
const HANDLING_OPTIONS = ['reship', 'refund'];
const FAULT_OPTIONS = ['center', 'courier', 'shipper'];

const sectionCard = { ...card, marginTop: '14px' };
const sectionTitle = { margin: '0 0 10px', fontSize: '15px', fontWeight: 600, color: COLORS.text };
const subTitle = { margin: '14px 0 6px', fontSize: '13px', fontWeight: 600, color: COLORS.text };
const fieldBox = { display: 'flex', flexDirection: 'column', gap: '4px', fontSize: '12px', color: COLORS.textSub, minWidth: '130px', flex: '1 1 130px' };
const grid = { display: 'flex', flexWrap: 'wrap', gap: '10px 12px', alignItems: 'flex-end' };
const th = { padding: '6px 8px', textAlign: 'left', fontSize: '12px', fontWeight: 400, color: COLORS.textMuted, borderBottom: `1px solid ${COLORS.borderSoft}`, whiteSpace: 'nowrap' };
const td = { padding: '6px 8px', fontSize: '13px', color: COLORS.text, borderBottom: `1px solid ${COLORS.borderSoft}`, verticalAlign: 'middle' };
const errText = { color: COLORS.dangerText, fontSize: '12px' };
const note = { fontSize: '12px', color: COLORS.textSub, margin: '6px 0 0' };
const sub = { fontSize: '12px', color: COLORS.textSub };
const smallInput = { ...input, padding: '5px 8px', width: '80px' };
const linkBtn = { border: 0, background: 'transparent', padding: 0, cursor: 'pointer', color: COLORS.dangerText, fontWeight: 600, fontSize: '13px', textDecoration: 'underline' };
const track = { height: '8px', borderRadius: '4px', background: TONES.gray.soft, overflow: 'hidden', marginTop: '4px' };
const fillBar = (pct, color) => ({ width: `${Math.max(0, Math.min(100, pct))}%`, height: '100%', background: color, transition: 'width .3s' });
const thumb = (filled) => ({ width: '20px', height: '20px', borderRadius: '3px', border: `1px solid ${COLORS.border}`, background: filled ? TONES.blue.soft : COLORS.surface });
const yellowNote = { marginTop: '10px', padding: '8px 10px', borderRadius: '5px', background: COLORS.warnSoft, color: COLORS.warn, fontSize: '12px' };
const dropZone = (over) => ({
  marginTop: '10px', padding: '18px', borderRadius: '8px', textAlign: 'center', fontSize: '13px', color: COLORS.textSub,
  border: `1.5px dashed ${over ? COLORS.primary : COLORS.border}`, background: over ? COLORS.primarySoft : COLORS.surface,
});
const summaryBox = (tone) => ({ flex: '1 1 60px', padding: '8px 10px', borderRadius: '6px', background: TONES[tone].soft, color: TONES[tone].ink, fontSize: '12px' });
const filterChip = (on, tone) => ({ ...badge(on ? tone : 'gray'), border: on ? `1px solid ${TONES[tone].ink}` : '1px solid transparent', cursor: 'pointer' });
const orangeBtn = { background: TONES.orange.ink, color: '#fff', width: '100%' };
const stop = (e) => e.stopPropagation();

function Field({ label, children }) {
  return <label style={fieldBox}><span>{label}</span>{children}</label>;
}

// 저장된 사진 미리보기 — 링크로 열면 토큰이 안 실린다 → blob 으로 받는다(object URL 을 못 만드는 환경이면 이름만).
function PhotoThumb({ claimId, photo }) {
  const [url, setUrl] = useState('');
  useEffect(() => {
    if (typeof URL === 'undefined' || typeof URL.createObjectURL !== 'function') return undefined;
    let alive = true;
    let made = '';
    (async () => {
      try {
        const r = await claimLedgerAPI.photo(claimId, photo.id);
        if (!alive) return;
        made = URL.createObjectURL(r.data);
        setUrl(made);
      } catch (e) { /* 미리보기만 못 그린다 — 이름은 보인다 */ }
    })();
    return () => {
      alive = false;
      if (made) URL.revokeObjectURL(made);
    };
  }, [claimId, photo.id]);
  return url
    ? <img src={url} alt={photo.original_name || ''} style={{ width: '22px', height: '22px', objectFit: 'cover', borderRadius: '3px' }} />
    : null;
}

export default function ClaimBulkPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const uploadId = searchParams.get('upload');
  const [forbidden, setForbidden] = useState(false);
  const [history, setHistory] = useState(null);
  const [upload, setUpload] = useState(null);
  const [openError, setOpenError] = useState('');
  const [fileError, setFileError] = useState('');
  const [uploading, setUploading] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const [level, setLevel] = useState('');
  const [page, setPage] = useState(0);
  const [rowsData, setRowsData] = useState(EMPTY_ROWS);
  const [pollTick, setPollTick] = useState(0);
  const [failedRows, setFailedRows] = useState([]);
  const [detail, setDetail] = useState(null);           // 행 상세(row_detail)
  const [panelStart, setPanelStart] = useState(null);   // 상세 패널 시작 값(바뀐 칸 비교)
  const [panel, setPanel] = useState(null);             // 상세 패널 지금 값
  const [panelError, setPanelError] = useState('');
  const [applying, setApplying] = useState(false);
  const [form, setForm] = useState(null);               // claimLedgerAPI.form — 상세 패널을 처음 열 때 1회
  const [busy, setBusy] = useState('');                 // template | errors | register | save | cancel | revalidate
  const pollSeq = useRef(0);
  const formAsked = useRef(false);

  const live = isLive(upload);

  // ── 업로드 이력(?upload= 없을 때) ──
  const loadHistory = useCallback(async () => {
    try {
      const r = await claimBulkAPI.list();
      setHistory((r.data && r.data.items) || []);
    } catch (e) {
      if (statusOf(e) === 403) { setForbidden(true); return; }
      setHistory([]);
      toast(errorText(e, '업로드 이력을 불러오지 못했습니다'), 'error');
    }
  }, []);

  useEffect(() => {
    if (!uploadId) loadHistory();
  }, [uploadId, loadHistory]);

  // ── 업로드 1건 열기 ──
  useEffect(() => {
    setUpload(null); setOpenError(''); setDetail(null); setPanel(null); setPanelStart(null); setPanelError('');
    setLevel(''); setPage(0); setRowsData(EMPTY_ROWS); setFailedRows([]); setFileError('');
    if (!uploadId) return undefined;
    let alive = true;
    const seq = pollSeq.current + 1;
    pollSeq.current = seq;
    (async () => {
      try {
        const r = await claimBulkAPI.get(uploadId);
        if (alive && seq === pollSeq.current) setUpload(r.data || null);
      } catch (e) {
        if (!alive) return;
        if (statusOf(e) === 403) { setForbidden(true); return; }
        setOpenError(errorText(e, '일괄 등록을 불러오지 못했습니다'));
      }
    })();
    return () => { alive = false; };
  }, [uploadId]);

  // 요약 다시 읽기(행 수정·사진·선택 뒤) — 마지막 요청의 응답만 쓴다.
  const refreshUpload = useCallback(async () => {
    if (!uploadId) return;
    const seq = pollSeq.current + 1;
    pollSeq.current = seq;
    try {
      const r = await claimBulkAPI.get(uploadId);
      if (seq === pollSeq.current) setUpload(r.data || null);
    } catch (e) { /* 요약은 다음 동작·폴링에서 다시 읽는다 */ }
  }, [uploadId]);

  // 서버 응답으로 바로 그릴 때 — 그 전에 나간 폴링 응답이 덮지 않게 순번을 올린다.
  const putUpload = (data) => {
    pollSeq.current += 1;
    setUpload(data || null);
  };

  // ── 진행 중(queued·validating·registering)이면 1.5초마다 GET /{id} ──
  useEffect(() => {
    if (!uploadId || !live) return undefined;
    let alive = true;
    const timer = setTimeout(async () => {
      const seq = pollSeq.current + 1;
      pollSeq.current = seq;
      try {
        const r = await claimBulkAPI.get(uploadId);
        if (alive && seq === pollSeq.current) setUpload(r.data || null);
      } catch (e) {
        if (!alive || seq !== pollSeq.current) return;
        if (statusOf(e) === 404) { setUpload(null); setOpenError(errorText(e, '일괄 등록을 찾을 수 없습니다')); return; }
        setPollTick((t) => t + 1);                   // 한 번 실패해도 다음 주기에 다시 읽는다
      }
    }, POLL_MS);
    return () => { alive = false; clearTimeout(timer); };
  }, [uploadId, live, upload, pollTick]);

  // ── ③ 표 — 검증·등록이 끝난 상태가 바뀌거나 필터·페이지가 바뀌면 다시 읽는다 ──
  const settled = upload && !live ? `${upload.status}|${upload.finished_at || ''}` : '';
  useEffect(() => {
    if (!uploadId || !settled) return undefined;
    let alive = true;
    (async () => {
      const params = { offset: page * PAGE_SIZE, limit: PAGE_SIZE };
      if (level) params.level = level;
      try {
        const r = await claimBulkAPI.rows(uploadId, params);
        if (alive) setRowsData(r.data || EMPTY_ROWS);
      } catch (e) {
        if (alive) toast(errorText(e, '행을 불러오지 못했습니다'), 'error');
      }
    })();
    return () => { alive = false; };
  }, [uploadId, settled, level, page]);

  // ── 등록 실패 행 사유(결과 카드) — 실패가 있을 때만 앞 200행에서 찾는다 ──
  const failedCount = upload && upload.register ? Number(upload.register.failed) || 0 : 0;
  useEffect(() => {
    if (!uploadId || !settled || !failedCount) { setFailedRows([]); return undefined; }
    let alive = true;
    (async () => {
      try {
        const r = await claimBulkAPI.rows(uploadId, { offset: 0, limit: FAILED_PAGE });
        if (alive) setFailedRows(((r.data && r.data.items) || []).filter((row) => row.register_status === 'failed'));
      } catch (e) { /* 건수는 위에 보인다 — 사유 목록만 못 그린다 */ }
    })();
    return () => { alive = false; };
  }, [uploadId, settled, failedCount]);

  const replaceRow = (next) => {
    if (!next || next.id == null) return;
    setRowsData((d) => ({ ...d, items: d.items.map((r) => (r.id === next.id ? { ...r, ...next } : r)) }));
  };

  // ── 업로드 ──
  const onFile = async (file) => {
    setFileError('');
    const problem = checkUploadFile(file);
    if (problem) { setFileError(problem); return; }
    setUploading(true);
    try {
      const r = await claimBulkAPI.upload(file);
      const id = r.data && r.data.upload_id;
      if (id != null) setSearchParams({ upload: String(id) });
    } catch (e) {
      setFileError(errorText(e, '엑셀을 올리지 못했습니다'));
    } finally {
      setUploading(false);
    }
  };
  const onDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer && e.dataTransfer.files ? e.dataTransfer.files[0] : null;
    if (file) onFile(file);
  };

  const download = async (kind, call, fallback, failText) => {
    if (busy) return;
    setBusy(kind);
    try {
      const r = await call();
      blobDownload(r.data, fileNameOf(r, fallback));
    } catch (e) {
      toast(errorText(e, failText), 'error');
    } finally {
      setBusy('');
    }
  };
  const onTemplate = () => download('template', () => claimBulkAPI.template(), TEMPLATE_FALLBACK, '양식을 내려받지 못했습니다');
  const onErrors = () => download('errors', () => claimBulkAPI.errors(upload.id), `클레임_일괄등록_오류_${upload.id}.xlsx`,
    '오류 내역을 내려받지 못했습니다');

  // ── 행 선택 · 상세 ──
  const toggleSelect = async (row) => {
    try {
      const r = await claimBulkAPI.select(upload.id, [row.id], !row.selected);
      const data = r.data || {};
      const skipped = (data.skipped || []).includes(row.id);
      if (!skipped) replaceRow({ id: row.id, selected: !row.selected });
      if (data.selection) setUpload((u) => (u ? { ...u, selection: data.selection } : u));
      if (skipped) toast('이 행은 고를 수 없습니다', 'error');
    } catch (e) {
      toast(errorText(e, '선택을 바꾸지 못했습니다'), 'error');
    }
  };

  const showDetail = (data) => {
    const start = panelFromDetail(data);
    setDetail(data);
    setPanelStart(start);
    setPanel(start);
  };

  const openRow = async (row) => {
    setPanelError('');
    try {
      const r = await claimBulkAPI.row(upload.id, row.id);
      showDetail(r.data || null);
    } catch (e) {
      toast(errorText(e, '행을 불러오지 못했습니다'), 'error');
      return;
    }
    if (!formAsked.current) {
      formAsked.current = true;
      try {
        const f = await claimLedgerAPI.form();
        setForm(f.data || {});
      } catch (e) {
        formAsked.current = false;                   // 다음에 열 때 다시 읽는다
      }
    }
  };

  const onApply = async () => {
    if (!detail || !panel || applying) return;
    const edits = panelEdits(panelStart, panel);
    if (!Object.keys(edits).length) { toast('바뀐 칸이 없습니다'); return; }
    setApplying(true);
    setPanelError('');
    try {
      const r = await claimBulkAPI.patchRow(upload.id, detail.id, edits);
      showDetail(r.data || null);
      replaceRow(rowOf(r.data));
      refreshUpload();
      toast('적용했습니다');
    } catch (e) {
      const msg = errorText(e, '적용하지 못했습니다');
      setPanelError(msg);
      toast(msg, 'error');
    } finally {
      setApplying(false);
    }
  };

  const addPhoto = async (row, file) => {
    if (!file) return;
    try {
      const r = await claimBulkAPI.uploadPhoto(upload.id, row.id, file);
      const fresh = r.data && r.data.row;
      if (fresh) {
        replaceRow(rowOf(fresh));
        setDetail((d) => (d && d.id === fresh.id ? fresh : d));
      }
      refreshUpload();
      toast('사진을 올렸습니다');
    } catch (e) {
      toast(errorText(e, '사진을 올리지 못했습니다'), 'error');
    }
  };

  const removePhoto = async (photo) => {
    if (!detail || !detail.claim_id) return;
    try {
      await claimLedgerAPI.removePhoto(detail.claim_id, photo.id);
      const r = await claimBulkAPI.row(upload.id, detail.id);
      if (r.data) {
        setDetail(r.data);
        replaceRow(rowOf(r.data));
      }
      refreshUpload();
    } catch (e) {
      toast(errorText(e, '사진을 지우지 못했습니다'), 'error');
    }
  };

  // ── ⑤⑥⑦ ──
  const onRegister = async () => {
    const selection = (upload && upload.selection) || {};
    if (!selection.selected || busy) return;
    if (!(await confirmDialog(registerConfirmText(selection)))) return;
    setBusy('register');
    try {
      const r = await claimBulkAPI.register(upload.id);
      const data = r.data || {};
      setDetail(null); setPanel(null); setPanelStart(null);
      pollSeq.current += 1;
      // 202 응답의 상태로 바로 그린다 — 등록 중은 서버가 고치기·등록·취소를 받지 않는다(다음 폴링이 서버 값으로 덮는다).
      setUpload((u) => (u ? {
        ...u, status: data.status || 'registering', can_edit: false, can_register: false, can_cancel: false,
        register: { ...(u.register || {}), total: data.register_total ?? 0, done: 0 },
      } : u));
    } catch (e) {
      toast(errorText(e, '등록을 시작하지 못했습니다'), 'error');
    } finally {
      setBusy('');
    }
  };

  const onRevalidate = async () => {
    if (busy) return;
    setBusy('revalidate');
    try {
      const r = await claimBulkAPI.revalidate(upload.id);
      pollSeq.current += 1;
      setUpload((u) => (u ? { ...u, status: (r.data && r.data.status) || 'queued' } : u));
    } catch (e) {
      toast(errorText(e, '다시 검증하지 못했습니다'), 'error');
    } finally {
      setBusy('');
    }
  };

  const onSave = async () => {
    if (busy) return;
    setBusy('save');
    try {
      const r = await claimBulkAPI.save(upload.id);
      putUpload(r.data);
      toast('임시저장했습니다');
    } catch (e) {
      toast(errorText(e, '임시저장하지 못했습니다'), 'error');
    } finally {
      setBusy('');
    }
  };

  const onCancel = async () => {
    if (busy) return;
    if (!(await confirmDialog(CANCEL_CONFIRM, { danger: true }))) return;
    setBusy('cancel');
    try {
      await claimBulkAPI.cancel(upload.id);
      toast('취소했습니다');
      setSearchParams({});
    } catch (e) {
      toast(errorText(e, '취소하지 못했습니다'), 'error');
    } finally {
      setBusy('');
    }
  };

  // ── 표시 값 ──
  const counts = summaryCounts(upload);
  const evidence = evidenceProgress(upload);
  const selection = (upload && upload.selection) || {};
  const progress = live ? progressOf(upload) : null;
  const items = rowsData.items || [];
  const pages = Math.max(1, Math.ceil((Number(rowsData.total) || 0) / PAGE_SIZE));
  const showRevalidate = !!upload && upload.can_revalidate && (upload.status === 'failed'
    || items.some((r) => (r.issues || []).some((i) => i.code === 'lookup_error')));
  const hasResult = !!upload && !live && Number((upload.register || {}).total) > 0;

  // 상세 패널 값
  const reasons = (form && form.reasons) || [];
  const labels = (form && form.rule_labels) || null;
  const snapshot = detail ? detail.snapshot : null;
  const ship = snapshot && snapshot.found ? snapshot.shipment || {} : null;
  const editable = !!(detail && detail.editable && upload && upload.can_edit && snapshot && snapshot.found && !snapshot.too_large);
  const panelReason = panel ? reasons.find((r) => String(r.id) === String(panel.reason_id)) || null : null;
  const reasonOptions = panel ? reasons.filter((r) => r.accident_type === panel.accident) : [];
  const reasonMissing = panel && panel.reason_id && !reasonOptions.some((r) => String(r.id) === panel.reason_id);
  const centers = (form && form.centers) || [];
  const centerMissing = panel && panel.center_key && !centers.some((c) => c.center_key === panel.center_key);
  const intakeOptions = optionsWithCurrent(form && form.intake_channel_options, panel ? panel.intake_channel : '');
  const compensationOptions = optionsWithCurrent(form && form.compensation_method_options, panel ? panel.compensation_method : '');
  const preview = detail ? detail.preview : null;
  const calc = preview && preview.calc;
  const deadlines = preview && preview.deadlines;
  const approval = preview && preview.approval;
  const detailEvidence = (detail && detail.evidence) || { count: 0, min: 0 };
  const canPhoto = (row) => canSelectRow(row, upload);     // 사진도 고를 수 있는 행(정상·경고 · 미등록 · 고칠 수 있는 업로드)만

  const setP = (patch) => setPanel((p) => ({ ...p, ...patch }));
  const pickPanelReason = (id) => {
    const r = reasons.find((x) => String(x.id) === String(id));
    setP({
      reason_id: id ? String(id) : '',
      fault: r ? (r.default_fault || '') : '',
      extent: r && r.accident_type === 'general' && r.comp_scope === 'select' ? panel.extent : '',
      ...(r ? { accident: r.accident_type } : {}),
    });
  };
  const updateLine = (key, patch) => setP({ items: panel.items.map((it) => (it.line_key === key ? { ...it, ...patch } : it)) });
  const toggleLine = (it) => {
    const selected = !it.selected;
    const qty = selected && !(Number(it.qty) > 0) ? String(it.out_qty || 1) : it.qty;
    updateLine(it.line_key, { selected, qty });
  };

  const approvalText = !approval ? '—'
    : !approval.needed ? '승인 불필요'
      : approval.self_approve ? '본인 결재(센터장)'
        : (approval.approvers || []).length ? `${approval.approvers.join(', ')} (자동지정)` : '지정할 승인자가 없습니다';

  return (
    <AppShell app="claim-register">
      <main style={{ flex: 1, minWidth: 0, height: 'var(--gw-app-h, 100dvh)', overflow: 'auto', background: `var(--gw-bg, ${COLORS.bg})` }}>
        <Page>
          <nav aria-label="경로" data-testid="claim-bulk-crumb" style={{ fontSize: '12px', color: COLORS.textMuted, marginBottom: '4px' }}>
            클레임 관리 › 클레임 등록 › 엑셀 일괄등록
          </nav>
          <PageHeader icon={IconFileInvoice} tone="red" title="엑셀 일괄등록" subtitle="단건 등록과 동일한 항목을 엑셀로 한 번에 등록합니다"
            actions={(
              <>
                <Button variant="secondary" data-testid="claim-bulk-back" onClick={() => navigate('/claims/register')}>← 단건 등록</Button>
                <Button data-testid="claim-bulk-template" onClick={onTemplate} disabled={busy === 'template'}>① 양식 다운로드(.xlsx)</Button>
              </>
            )} />
          {forbidden && <div role="alert" style={{ ...errText, fontSize: '13px' }}>이 화면을 볼 권한이 없습니다.</div>}
          {!forbidden && (
            <>
              {openError && <div role="alert" data-testid="claim-bulk-open-error" style={{ ...errText, fontSize: '13px' }}>{openError}</div>}
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0 16px', alignItems: 'flex-start' }}>
                <div style={{ flex: '3 1 620px', minWidth: 0 }}>
                  {/* ② 엑셀 업로드 */}
                  <div role="region" aria-label="② 엑셀 업로드" style={sectionCard}>
                    <h2 style={sectionTitle}>② 엑셀 업로드</h2>
                    {upload && (
                      <div data-testid="claim-bulk-upload-info" style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', alignItems: 'center', fontSize: '13px' }}>
                        <strong>{upload.file_name}</strong>
                        <span style={sub}>{uploadInfoText(upload, fmtKstShort(upload.created_at))}</span>
                        <span data-testid="claim-bulk-status" data-status={upload.status} style={badge(UPLOAD_STATUS_TONE[upload.status] || 'gray')}>
                          {upload.status === 'ready' ? '업로드 완료' : (UPLOAD_STATUS[upload.status] || upload.status)}
                        </span>
                      </div>
                    )}
                    {upload && upload.message && <p style={{ ...note, color: upload.status === 'failed' ? COLORS.dangerText : COLORS.textSub }}>{upload.message}</p>}
                    {progress && (
                      <div data-testid="claim-bulk-progress" role="progressbar" aria-label={progress.label} aria-valuemin={0} aria-valuemax={100}
                        aria-valuenow={progress.pct} style={{ marginTop: '10px' }}>
                        <div style={sub}>{`${progress.label} ${progress.done} / ${progress.total}`}</div>
                        <div style={track}><div style={fillBar(progress.pct, COLORS.primary)} /></div>
                      </div>
                    )}
                    <div data-testid="claim-bulk-drop" style={dropZone(dragOver)}
                      onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
                      onDragLeave={() => setDragOver(false)}
                      onDrop={onDrop}>
                      <span>{upload ? '다른 파일을 끌어다 놓거나 ' : '엑셀 파일을 끌어다 놓거나 '}</span>
                      <label style={{ ...btn('secondary', 'sm'), display: 'inline-block', cursor: uploading ? 'default' : 'pointer' }}>
                        파일 선택
                        <input type="file" accept=".xlsx" aria-label="엑셀 파일 선택" data-testid="claim-bulk-file" disabled={uploading}
                          style={{ display: 'none' }}
                          onChange={(e) => { const file = e.target.files && e.target.files[0]; e.target.value = ''; if (file) onFile(file); }} />
                      </label>
                      <div style={note}>.xlsx · 2MB 이하 · 최대 1,000건 · 송장 1건 = 1행</div>
                    </div>
                    {uploading && <div role="status" style={note}>올리는 중…</div>}
                    {fileError && <div role="alert" data-testid="claim-bulk-upload-error" style={{ ...errText, marginTop: '8px' }}>{fileError}</div>}
                  </div>

                  {/* ③ 검증 결과 · 증빙사진 첨부 */}
                  {upload && (
                    <div role="region" aria-label="③ 검증 결과 · 증빙사진 첨부" style={sectionCard}>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', justifyContent: 'space-between', alignItems: 'baseline' }}>
                        <h2 style={{ ...sectionTitle, margin: 0 }}>
                          ③ 검증 결과 · 증빙사진 첨부 <span style={{ ...sub, fontWeight: 400 }}>송장 1건 = 1행 · 행 클릭 시 상세 패널</span>
                        </h2>
                        <div role="group" aria-label="판정 필터" style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                          {LEVEL_FILTERS.map((f) => (
                            <button key={f.id} type="button" data-testid={`claim-bulk-filter-${f.id}`} aria-pressed={level === f.level}
                              onClick={() => { setLevel(f.level); setPage(0); }} style={filterChip(level === f.level, f.tone)}>
                              {`${f.label} ${counts[f.id]}`}
                            </button>
                          ))}
                        </div>
                      </div>
                      {live && <p style={note}>검증·등록이 끝나면 표를 다시 그립니다(이 화면은 그대로 써도 됩니다).</p>}
                      <div style={{ overflowX: 'auto', marginTop: '10px' }}>
                        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                          <thead>
                            <tr>
                              <th style={th}>선택</th><th style={th}>송장번호 / 고객사</th><th style={th}>상품 × 수량</th><th style={th}>구분</th>
                              <th style={th}>사유</th><th style={th}>금액(VAT 포함)</th><th style={th}>증빙사진</th><th style={th}>검증</th>
                            </tr>
                          </thead>
                          <tbody>
                            {items.map((row) => {
                              const summary = row.summary || {};
                              const ev = row.evidence || { count: 0 };
                              const st = rowStatus(row);
                              const isError = row.level === 'error';
                              const open = detail && detail.id === row.id;
                              return (
                                <tr key={row.id} data-testid="claim-bulk-row" data-level={row.level} data-row-id={row.id}
                                  data-open={open ? 'true' : 'false'} onClick={() => openRow(row)}
                                  style={{ cursor: 'pointer', background: isError ? COLORS.dangerSoft : open ? COLORS.primarySoft : undefined }}>
                                  <td style={td} onClick={stop}>
                                    <input type="checkbox" aria-label={`${row.row_no}행 선택`} checked={!!row.selected}
                                      disabled={!canSelectRow(row, upload)} onChange={() => toggleSelect(row)} />
                                  </td>
                                  <td style={td}>
                                    {isError ? (
                                      <button type="button" data-testid="claim-bulk-fix" title="단건 화면에서 고쳐 등록합니다" style={linkBtn}
                                        onClick={(e) => { e.stopPropagation(); navigate(fixPath(upload.id, row.id)); }}>
                                        {row.invoice_no || '(송장 없음)'}
                                      </button>
                                    ) : <span>{row.invoice_no}</span>}
                                    <div style={sub}>{summary.shipper_name || '—'}</div>
                                  </td>
                                  <td style={td}>{summary.products || '—'}</td>
                                  <td style={td}>
                                    {summary.accident ? <span style={badge(ACCIDENT_TONE[summary.accident] || 'gray')}>{ACCIDENT_SHORT[summary.accident] || summary.accident}</span> : '—'}
                                  </td>
                                  <td style={td}>{summary.reason || '—'}</td>
                                  <td style={td}>{fmtWon(summary.total_amount_vat)}</td>
                                  <td style={td} onClick={stop}>
                                    <span data-testid="claim-bulk-evidence" data-count={ev.count || 0} style={{ display: 'inline-flex', gap: '3px', alignItems: 'center', verticalAlign: 'middle' }}>
                                      {[0, 1, 2].map((i) => <span key={i} style={thumb(i < (ev.count || 0))} />)}
                                      {(ev.count || 0) > 3 && <span style={sub}>{`+${ev.count - 3}`}</span>}
                                    </span>
                                    <label style={{ ...badge(canPhoto(row) ? 'blue' : 'gray'), marginLeft: '6px', cursor: canPhoto(row) ? 'pointer' : 'default' }}>
                                      첨부
                                      <input type="file" accept="image/*" aria-label={`${row.row_no}행 사진 첨부`} disabled={!canPhoto(row)} style={{ display: 'none' }}
                                        onChange={(e) => { const file = e.target.files && e.target.files[0]; e.target.value = ''; addPhoto(row, file); }} />
                                    </label>
                                  </td>
                                  <td style={td}>
                                    <span data-testid="claim-bulk-check" title={st.title || undefined} style={badge(st.tone)}>{st.label}</span>
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', alignItems: 'center', justifyContent: 'space-between', marginTop: '8px' }}>
                        <span data-testid="claim-bulk-shown" style={sub}>{`${Number(rowsData.total) || 0}건 중 ${items.length}건 표시`}</span>
                        {pages > 1 && (
                          <div role="group" aria-label="페이지" style={{ display: 'flex', gap: '4px', flexWrap: 'wrap' }}>
                            {Array.from({ length: pages }, (_, i) => (
                              <Button key={i} size="sm" variant={i === page ? 'primary' : 'secondary'} aria-current={i === page ? 'page' : undefined}
                                onClick={() => setPage(i)}>{String(i + 1)}</Button>
                            ))}
                          </div>
                        )}
                      </div>
                      <div style={yellowNote}>행을 클릭하면 우측 상세 패널이 열려 건별 수정·사진 첨부를 할 수 있습니다</div>
                    </div>
                  )}

                  {/* 업로드 이력 */}
                  {!uploadId && (
                    <div role="region" aria-label="업로드 이력" style={sectionCard}>
                      <h2 style={sectionTitle}>업로드 이력</h2>
                      {history === null && <div role="status" style={sub}>불러오는 중…</div>}
                      {history && history.length === 0 && <p style={note}>올린 엑셀이 없습니다.</p>}
                      {history && history.length > 0 && (
                        <div style={{ overflowX: 'auto' }}>
                          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                            <thead>
                              <tr><th style={th}>파일명</th><th style={th}>건수</th><th style={th}>결과</th><th style={th}>등록자</th><th style={th}>시각</th><th style={th}>상태</th></tr>
                            </thead>
                            <tbody>
                              {history.map((h) => (
                                <tr key={h.id} data-testid="claim-bulk-history-row" data-upload-id={h.id} style={{ cursor: 'pointer' }}
                                  onClick={() => setSearchParams({ upload: String(h.id) })}>
                                  <td style={td}>{h.file_name}</td>
                                  <td style={td}>{`${Number(h.total_rows) || 0}건`}</td>
                                  <td style={td}>{historyResultText(h)}</td>
                                  <td style={td}>{h.created_by_name || '—'}</td>
                                  <td style={td}>{fmtKstShort(h.created_at)}</td>
                                  <td style={td}><span data-testid="claim-bulk-history-status" style={badge(UPLOAD_STATUS_TONE[h.status] || 'gray')}>{UPLOAD_STATUS[h.status] || h.status}</span></td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      )}
                    </div>
                  )}
                </div>

                {/* 오른쪽 열 */}
                {upload && (
                  <div style={{ flex: '2 1 320px', minWidth: 0 }}>
                    {/* 상세 패널 */}
                    {detail && panel && (
                      <div role="region" aria-label="상세 패널" data-testid="claim-bulk-detail" data-row-id={detail.id} style={sectionCard}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: '8px' }}>
                          <h2 style={{ ...sectionTitle, margin: 0 }}>{`${detail.row_no}행 · ${detail.invoice_no || '송장 없음'}`}</h2>
                          <Button size="sm" variant="quiet" onClick={() => { setDetail(null); setPanel(null); setPanelStart(null); }}>닫기</Button>
                        </div>
                        {(detail.issues || []).length > 0 && (
                          <ul style={{ ...note, paddingLeft: '18px' }}>
                            {detail.issues.map((i, n) => (
                              <li key={`${i.code}-${n}`} style={{ color: i.level === 'error' ? COLORS.dangerText : COLORS.warn }}>{i.message}</li>
                            ))}
                          </ul>
                        )}
                        <h3 style={subTitle}>출고정보</h3>
                        {ship ? (
                          <div data-testid="claim-bulk-detail-ship" style={{ fontSize: '12px', color: COLORS.textSub, lineHeight: 1.7 }}>
                            <div>{`센터 ${ship.center_name || ship.center_key || '—'} · 고객사 ${ship.shipper_name || '—'}`}</div>
                            <div>{`출고일 ${ship.ship_date || '—'} · 배송완료일 ${ship.delivered_date || '—'}`}</div>
                            <div>{`택배사 ${ship.courier_name || ship.courier_code || '—'}${ship.order_no ? ` · 주문번호 ${ship.order_no}` : ''}`}</div>
                          </div>
                        ) : (
                          <p style={{ ...note, color: COLORS.warn }}>출고정보가 없습니다 — 송장 오류는 엑셀을 고쳐 다시 올리거나 송장을 눌러 단건 화면에서 등록하세요.</p>
                        )}
                        {ship && ship.center_unknown && (
                          <p data-testid="claim-bulk-center-unknown" style={{ ...note, color: COLORS.warn, fontWeight: 600 }}>
                            {`센터 대장에 없는 창고코드 ${ship.depot_cd || ''} — 센터를 고르세요`}
                          </p>
                        )}
                        <fieldset disabled={!editable} style={{ border: 0, padding: 0, margin: 0, minWidth: 0 }}>
                          {panel.items.length > 0 && (
                            <>
                              <h3 style={subTitle}>상품</h3>
                              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                                <thead>
                                  <tr><th style={th}>선택</th><th style={th}>상품명</th><th style={th}>출고</th><th style={th}>클레임 수량</th><th style={th}>단가 직접 입력</th></tr>
                                </thead>
                                <tbody>
                                  {panel.items.map((it, n) => (
                                    <tr key={it.line_key || `line-${n}`} data-testid="claim-bulk-detail-line">
                                      <td style={td}>
                                        <input type="checkbox" aria-label={`${it.prod_name || `${n + 1}번 줄`} 선택`} checked={it.selected}
                                          disabled={!it.line_key} onChange={() => toggleLine(it)} />
                                      </td>
                                      <td style={td}>{it.prod_name || it.prod_cd || '—'}</td>
                                      <td style={td}>{it.out_qty}</td>
                                      <td style={td}>
                                        <input aria-label="클레임 수량" inputMode="numeric" style={{ ...smallInput, width: '60px' }} value={it.qty} disabled={!it.line_key}
                                          onChange={(e) => updateLine(it.line_key, { qty: e.target.value })} />
                                      </td>
                                      <td style={td}>
                                        <input aria-label="단가 직접 입력" inputMode="numeric" style={smallInput} value={it.unit_price_input} disabled={!it.line_key}
                                          placeholder={it.pur_price != null ? String(it.pur_price) : ''}
                                          onChange={(e) => updateLine(it.line_key, { unit_price_input: e.target.value })} />
                                      </td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </>
                          )}
                          <div style={{ ...grid, marginTop: '10px' }}>
                            {(detail.v2_candidates || []).length > 0 && (
                              <Field label="2.0 클레임">
                                <select aria-label="2.0 클레임" style={input} value={panel.v2_claim_no} onChange={(e) => setP({ v2_claim_no: e.target.value })}>
                                  <option value="">연결 안 함</option>
                                  {detail.v2_candidates.map((c) => (
                                    <option key={c.v2_claim_no} value={c.v2_claim_no} disabled={c.registered && c.v2_claim_no !== panel.v2_claim_no}>
                                      {`${c.v2_claim_no}${c.kind === 'exchange' ? ' 교환' : c.kind === 'return' ? ' 반품' : ''}${c.registered ? ' (등록됨)' : ''}`}
                                    </option>
                                  ))}
                                </select>
                              </Field>
                            )}
                            <Field label="사고구분">
                              <select aria-label="사고구분" style={input} value={panel.accident}
                                onChange={(e) => setP({ accident: e.target.value, reason_id: '', fault: '', extent: '' })}>
                                {Object.entries(ACCIDENT_LABEL).map(([key, label]) => <option key={key} value={key}>{label}</option>)}
                              </select>
                            </Field>
                            <Field label="클레임 사유">
                              <select aria-label="클레임 사유" style={input} value={panel.reason_id} onChange={(e) => pickPanelReason(e.target.value)}>
                                <option value="">사유 선택</option>
                                {reasonOptions.map((r) => <option key={r.id} value={String(r.id)}>{`${r.code} ${r.name}`}</option>)}
                                {reasonMissing && <option value={panel.reason_id}>{(detail.summary && detail.summary.reason) || `사유 ${panel.reason_id}`}</option>}
                              </select>
                            </Field>
                            {panelReason && panelReason.accident_type === 'general' && panelReason.comp_scope === 'select' && (
                              <Field label="범위">
                                <select aria-label="범위" style={input} value={panel.extent} onChange={(e) => setP({ extent: e.target.value })}>
                                  <option value="">범위 선택</option>
                                  <option value="partial">일부</option>
                                  <option value="all">전제품</option>
                                </select>
                              </Field>
                            )}
                            <Field label="귀책">
                              <select aria-label="귀책" style={input} value={panel.fault} onChange={(e) => setP({ fault: e.target.value })}>
                                <option value="">귀책 선택</option>
                                {FAULT_OPTIONS.map((f) => <option key={f} value={f}>{labelOf('fault', f, labels)}</option>)}
                              </select>
                            </Field>
                            <Field label="처리 방식">
                              <select aria-label="처리 방식" style={input} value={panel.handling} onChange={(e) => setP({ handling: e.target.value })}>
                                <option value="">처리 방식 선택</option>
                                {HANDLING_OPTIONS.map((h) => <option key={h} value={h}>{labelOf('handling', h, labels)}</option>)}
                              </select>
                            </Field>
                            <Field label="품온">
                              <select aria-label="품온" style={input} value={panel.temp_zone} onChange={(e) => setP({ temp_zone: e.target.value })}>
                                <option value="">품온 선택</option>
                                {ZONE_OPTIONS.map((z) => <option key={z.key} value={z.key}>{z.label}</option>)}
                              </select>
                            </Field>
                            <Field label="센터">
                              <select aria-label="센터" style={input} value={panel.center_key} onChange={(e) => setP({ center_key: e.target.value })}>
                                <option value="">센터 선택</option>
                                {centers.map((c) => <option key={c.center_key} value={c.center_key}>{c.display_name}</option>)}
                                {centerMissing && <option value={panel.center_key}>{panel.center_key}</option>}
                              </select>
                            </Field>
                            <Field label="접수 경로">
                              <select aria-label="접수 경로" style={input} value={panel.intake_channel} onChange={(e) => setP({ intake_channel: e.target.value })}>
                                <option value="">선택 안 함</option>
                                {intakeOptions.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                              </select>
                            </Field>
                            <Field label="배상 방식">
                              <select aria-label="배상 방식" style={input} value={panel.compensation_method} onChange={(e) => setP({ compensation_method: e.target.value })}>
                                <option value="">선택 안 함</option>
                                {compensationOptions.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                              </select>
                            </Field>
                          </div>
                          <label style={{ ...fieldBox, marginTop: '10px' }}>
                            <span>메모</span>
                            <textarea aria-label="메모" rows={2} style={{ ...input, resize: 'vertical' }} value={panel.memo} onChange={(e) => setP({ memo: e.target.value })} />
                          </label>
                        </fieldset>

                        {/* 금액 · 기한 · 승인 · 점검 — 서버 미리보기 그대로 */}
                        <h3 style={subTitle}>금액 · 기한 · 승인</h3>
                        {detail.preview_error && <div style={errText}>{detail.preview_error.message || '금액을 계산하지 못했습니다'}</div>}
                        {calc && calc.blocked && <div role="alert" style={{ ...errText, fontSize: '13px' }}>{calc.blocked.message}</div>}
                        <div data-testid="claim-bulk-detail-total" data-total-vat={calc && calc.total_amount_vat != null ? String(calc.total_amount_vat) : ''}
                          style={{ fontSize: '13px' }}>
                          {`클레임 금액 VAT 별도 ${fmtWon(calc ? calc.total_amount : null)} · VAT 포함 ${fmtWon(calc ? calc.total_amount_vat : null)}`}
                        </div>
                        <div style={sub}>{`고객사 접수기한 ${shipperDeadlineText(deadlines && deadlines.shipper)} · 택배사 접수기한 ${courierDeadlineText(deadlines && deadlines.courier)}`}</div>
                        <div data-testid="claim-bulk-detail-approval" style={sub}>{`승인 ${approvalText}`}</div>
                        {((preview && preview.checks) || []).map((c) => (
                          <div key={c.key} data-testid="claim-bulk-detail-check" data-ok={c.ok ? 'true' : 'false'}
                            style={{ display: 'flex', gap: '6px', fontSize: '12px', color: c.level === 'block' ? COLORS.dangerText : c.ok ? COLORS.text : COLORS.warn }}>
                            <span aria-hidden="true">{c.ok ? '✓' : '!'}</span>
                            <span>{c.label}{c.detail ? ` — ${c.detail}` : ''}</span>
                          </div>
                        ))}

                        {/* 증빙사진 */}
                        <h3 style={subTitle}>증빙사진</h3>
                        <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap', fontSize: '12px', color: COLORS.textSub }}>
                          <span data-testid="claim-bulk-detail-evidence">{`${detailEvidence.count || 0}장 / 최소 ${detailEvidence.min || 0}장`}</span>
                          {canPhoto(detail) && (
                            <label style={{ ...badge('blue'), cursor: 'pointer' }}>
                              + 사진
                              <input type="file" accept="image/*" aria-label="증빙사진 추가" style={{ display: 'none' }}
                                onChange={(e) => { const file = e.target.files && e.target.files[0]; e.target.value = ''; addPhoto(detail, file); }} />
                            </label>
                          )}
                        </div>
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', marginTop: '6px' }}>
                          {(detail.photos || []).map((p) => (
                            <span key={p.id} data-testid="claim-bulk-photo" style={{ ...badge('gray'), gap: '6px' }}>
                              {detail.claim_id && <PhotoThumb claimId={detail.claim_id} photo={p} />}
                              {p.original_name || `사진 ${p.id}`}
                              {canPhoto(detail) && (
                                <button type="button" aria-label={`${p.original_name || '사진'} 지우기`} onClick={() => removePhoto(p)}
                                  style={{ border: 0, background: 'transparent', cursor: 'pointer', color: COLORS.textSub }}>×</button>
                              )}
                            </span>
                          ))}
                        </div>
                        {panelError && <div role="alert" data-testid="claim-bulk-detail-error" style={{ ...errText, marginTop: '8px' }}>{panelError}</div>}
                        <div style={{ marginTop: '12px' }}>
                          <Button data-testid="claim-bulk-apply" onClick={onApply} disabled={!editable || applying}>{applying ? '적용 중…' : '적용'}</Button>
                        </div>
                      </div>
                    )}

                    {/* ⑤ 일괄 검증 요약 */}
                    <div role="region" aria-label="⑤ 일괄 검증 요약" style={sectionCard}>
                      <h2 style={sectionTitle}>⑤ 일괄 검증 요약</h2>
                      <div style={{ display: 'flex', gap: '6px' }}>
                        {LEVEL_FILTERS.map((f) => (
                          <div key={f.id} data-testid={`claim-bulk-summary-${f.id}`} style={summaryBox(f.tone)}>
                            <div>{f.label}</div>
                            <strong style={{ fontSize: '17px' }}>{counts[f.id]}</strong>
                          </div>
                        ))}
                      </div>
                      <p style={note}>오류 건은 등록에서 제외됩니다. 수정 후 재업로드해 주세요.</p>
                      <div style={{ display: 'flex', gap: '6px', marginTop: '8px', flexWrap: 'wrap' }}>
                        <Button variant="secondary" data-testid="claim-bulk-errors" onClick={onErrors} disabled={busy === 'errors' || !counts.error}>오류 내역 다운로드</Button>
                        {showRevalidate && (
                          <Button variant="secondary" data-testid="claim-bulk-revalidate" onClick={onRevalidate} disabled={busy === 'revalidate'}>다시 검증</Button>
                        )}
                      </div>
                    </div>

                    {/* ⑥ 증빙사진 첨부 현황 */}
                    <div role="region" aria-label="⑥ 증빙사진 첨부 현황" style={sectionCard}>
                      <h2 style={sectionTitle}>⑥ 증빙사진 첨부 현황</h2>
                      <div data-testid="claim-bulk-evidence-progress" style={{ display: 'flex', justifyContent: 'space-between', fontSize: '13px' }}>
                        <span>{`${evidence.done} / ${evidence.total}건 완료`}</span>
                        <strong>{`${evidence.pct}%`}</strong>
                      </div>
                      <div style={track}><div style={fillBar(evidence.pct, COLORS.success)} /></div>
                      <p style={note}>행의 [첨부]로 건별 업로드 · 행 클릭 → 상세 패널에서 첨부</p>
                    </div>

                    {/* ⑦ 선택 · 일괄 등록 */}
                    <div role="region" aria-label="⑦ 선택 · 일괄 등록" style={{ ...sectionCard, display: 'flex', flexDirection: 'column', gap: '8px' }}>
                      <div data-testid="claim-bulk-selection" style={{ fontSize: '13px', fontWeight: 600 }}>{selectionText(selection)}</div>
                      <Button data-testid="claim-bulk-register" onClick={onRegister} style={orangeBtn}
                        disabled={!Number(selection.selected) || !upload.can_register || busy === 'register'}>
                        {`선택 건 일괄 등록 (${Number(selection.selected) || 0}건)`}
                      </Button>
                      <p style={{ ...note, margin: 0 }}>모든 행에 단건과 같은 [등록(승인)] 판정</p>
                      <div style={{ display: 'flex', gap: '8px' }}>
                        <Button variant="secondary" data-testid="claim-bulk-save" style={{ flex: 1 }} onClick={onSave}
                          disabled={busy === 'save' || upload.status === 'cancelled'}>임시저장</Button>
                        <Button variant="secondary" data-testid="claim-bulk-cancel" style={{ flex: 1 }} onClick={onCancel}
                          disabled={busy === 'cancel' || !upload.can_cancel}>취소</Button>
                      </div>
                      {upload.saved_at && <p data-testid="claim-bulk-saved" style={{ ...note, margin: 0 }}>{`임시저장 ${fmtKstShort(upload.saved_at)}`}</p>}
                    </div>

                    {/* 일괄 등록 결과 */}
                    {hasResult && (
                      <div role="region" aria-label="일괄 등록 결과" data-testid="claim-bulk-result" style={sectionCard}>
                        <h2 style={sectionTitle}>일괄 등록 결과</h2>
                        <div data-testid="claim-bulk-result-counts" style={{ fontSize: '13px', fontWeight: 600 }}>{resultText(upload.register)}</div>
                        {failedRows.map((r) => (
                          <div key={r.id} data-testid="claim-bulk-result-failed" style={{ ...errText, marginTop: '4px' }}>
                            {`${r.row_no}행 ${r.invoice_no || ''} — ${r.register_error || '등록하지 못했습니다'}`}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            </>
          )}
        </Page>
      </main>
    </AppShell>
  );
}
